import { DustSpecies, SheetGoodKind, Species } from "../game/Materials";

/**
 * What each species looks like: a lighter body color and a darker one.
 *
 * These are the game's colors for a wood everywhere it appears — the
 * procedural sprites, the dust below, and the shipped face textures,
 * which `npm run process:textures` paints by building a ramp between
 * this pair (see the header of scripts/texture-stylize.ts). Changing a
 * species here and re-running is how its wood gets recolored.
 *
 * The values are measured from that species' own photography, at the two
 * luminance percentiles the ramp plants them on, by
 * `npx tsx scripts/sample-palettes.ts`. Re-run it after adding or
 * replacing source scans — but treat what it prints as a starting point,
 * not an answer. A photograph carries its own white balance, and where
 * the measurement disagrees with what the material is, the material
 * wins; particle board below is the standing example.
 */
export const colorBySpecies: Record<
  Species,
  { primary: string; secondary: string }
> = {
  pallet: { primary: "#83705D", secondary: "#624E41" },
  cherry: { primary: "#C35C26", secondary: "#A23F0E" },
  mahogany: { primary: "#7D230E", secondary: "#651807" },
  maple: { primary: "#F9D8B4", secondary: "#F3CDA2" },
  oak: { primary: "#AA7C58", secondary: "#9B7050" },
  pine: { primary: "#EFCBA0", secondary: "#EAC295" },
  poplar: { primary: "#DDD4C1", secondary: "#BAAD92" },
  walnut: { primary: "#7A482A", secondary: "#663C26" },
  purpleHeart: { primary: "#7D305A", secondary: "#6A244B" },
};

/**
 * Dust colors, species and sheet pseudo-species alike (see
 * docs/dust-and-cleaning.md). The sheet entries are deliberately nastier
 * than the sheets themselves: plywood sheds a gray-tan gluey chip, MDF a
 * dull beige powder that gets everywhere, the chip boards a dirty
 * resinous grit.
 */
export const dustColorBySpecies: Record<
  DustSpecies,
  { primary: string; secondary: string }
> = {
  ...colorBySpecies,
  plywood: { primary: "#C3B69A", secondary: "#9C8F74" },
  mdf: { primary: "#C6B49B", secondary: "#9B8A72" },
  osb: { primary: "#B79C6C", secondary: "#8C7449" },
  particleBoard: { primary: "#BFB4A6", secondary: "#958B7E" },
};

/**
 * The same, per sheet good, measured from each kind's own tile — so the
 * plywoods stopped borrowing the palettes of the species they resemble
 * and now carry the color of the veneer actually photographed.
 */
export const colorBySheetGoodKind: Record<
  SheetGoodKind,
  { primary: string; secondary: string }
> = {
  mdf: { primary: "#D0A56A", secondary: "#CB9E63" },
  osb: { primary: "#DEAC69", secondary: "#C68C47" },
  // White melamine facing. The measurement comes back faintly green —
  // the source tile's white balance, not the material — so this pair is
  // the neutral the photograph was reaching for.
  particleBoard: { primary: "#EAEBE8", secondary: "#D6D8D4" },
  plywoodA: { primary: "#E0C3A2", secondary: "#D8BB9A" },
  plywoodB: { primary: "#A99174", secondary: "#997855" },
  plywoodC: { primary: "#D5C3A9", secondary: "#C8B499" },
};

/** The strand palette OSB chips draw from — varied tans over the base. */
export const osbFlakeColors: ReadonlyArray<string> = [
  "#C9AC66",
  "#A98B4B",
  "#93773E",
  "#D2B87A",
];
